import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Autocomplete, CircularProgress, TextField } from "@mui/material"
import { Controller, useFormContext } from "react-hook-form"

import type { Span } from "types/spans.types"

import { nameOrUnnamed } from "helpers/utils/translations"

import { useSpansContext } from "./useSpansContext"
import { isTowerPairFieldMissing } from "./helpers"

interface SpanAutocompleteProps {
  name: string
  placeholder?: string
  initialData?: Span
}

const SpanAutocomplete = ({ name, placeholder, initialData }: SpanAutocompleteProps) => {
  const { control, watch } = useFormContext()
  const { t } = useTranslation()

  const powerlineId = watch("powerlineId")
  const towerAId = watch("towerAId")
  const towerBId = watch("towerBId")

  const { spansByTowerPair, isLoading } = useSpansContext()

  const options = useMemo(() => {
    if (!towerAId || !towerBId) return []

    const key = `${Math.min(Number(towerAId), Number(towerBId))}-${Math.max(Number(towerAId), Number(towerBId))}`
    const spans = spansByTowerPair[key] || []

    return spans.map((span) => ({
      id: span.id,
      label: `${nameOrUnnamed(span.name, t)} (${span.id})`,
    }))
  }, [spansByTowerPair, t, towerAId, towerBId])

  const disabled = !powerlineId || !towerAId || !towerBId

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (_value, values) =>
          !isTowerPairFieldMissing(name, values) || t("generic.FIELD_REQUIRED"),
      }}
      render={({ field, fieldState: { error } }) => {
        const { onChange, value, ref } = field

        const selectedOption = (() => {
          if (!value) return null
          const found = options.find((opt) => opt.id === value)
          if (found) return found
          if (initialData && initialData.id === value) {
            return {
              id: initialData.id,
              label: `${nameOrUnnamed(initialData.name, t)} (${initialData.id})`,
            }
          }
          return { id: value as number, label: String(value) }
        })()

        const optionsWithInitial =
          initialData &&
          initialData.id === value &&
          !options.some((opt) => opt.id === initialData.id)
            ? [
                {
                  id: initialData.id,
                  label: `${nameOrUnnamed(initialData.name, t)} (${initialData.id})`,
                },
                ...options,
              ]
            : options

        return (
          <Autocomplete
            options={optionsWithInitial}
            disabled={disabled}
            loading={isLoading}
            value={selectedOption}
            onChange={(_event, newValue) => {
              onChange(newValue ? newValue.id : null)
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.label || ""}
            noOptionsText={
              isLoading
                ? t("generic.LOADING")
                : !towerAId || !towerBId
                  ? t("device_installation.SELECT_TOWERS_FIRST")
                  : t("generic.NO_RESULTS_FOUND")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={ref}
                variant="outlined"
                size="small"
                placeholder={placeholder ?? ""}
                error={!!error}
                helperText={error?.message}
                sx={{ mb: 0 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )
      }}
    />
  )
}

export default SpanAutocomplete
